import {
  Sidebar,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarFooter,
} from "@/components/ui/sidebar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard,
  Store,
  User,
  Package,
  ChevronRight,
  LogOut,
  LucideIcon,
} from "lucide-react";
import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import useAuth from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

interface MenuItem {
  title: string;
  href: string;
  Icon: LucideIcon;
}

const menuItems: MenuItem[] = [
  {
    title: "Dashboard",
    href: "/portal",
    Icon: LayoutDashboard,
  },
  {
    title: "Talleres",
    href: "/talleres",
    Icon: Store,
  },
  {
    title: "Mi Perfil",
    href: "/perfil",
    Icon: User,
  },
];

export function ProveedorSidebar({
  ...props
}: React.ComponentProps<typeof Sidebar>) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logOut } = useAuth();

  const userEmail = user?.user?.email || "";
  const userName = user?.user?.name || user?.user?.email || "Proveedor";

  const handleLogout = async () => {
    await logOut();
    navigate("/auth/login");
  };

  const isActive = (href: string) => {
    if (href === "/portal") {
      return location.pathname === "/portal";
    }
    return (
      location.pathname === href || location.pathname.startsWith(href + "/")
    );
  };

  return (
    <Sidebar collapsible="offcanvas" {...props}>
      <SidebarHeader className="border-b border-sidebar-border bg-gradient-to-b from-white to-[hsl(var(--proveedor-surface))]">
        <div className="flex items-center gap-3 p-2">
          <div className="h-8 w-8 rounded-xl bg-[hsl(var(--proveedor-primary))] flex items-center justify-center shadow-sm">
            <Package className="h-4.5 w-4.5 text-white" />
          </div>
          <div className="flex flex-col">
            <span className="text-base font-semibold text-foreground leading-tight">
              Mecanica MX
            </span>
            <span className="text-[10px] text-muted-foreground leading-tight">
              Portal Proveedores
            </span>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent className="bg-gradient-to-b from-[hsl(var(--proveedor-surface))] to-white">
        <SidebarGroup>
          <SidebarGroupLabel className="text-[11px] uppercase tracking-wider text-muted-foreground/70 font-medium px-3 mb-1">
            Navegacion
          </SidebarGroupLabel>
          <SidebarGroupContent className="px-2">
            <SidebarMenu className="space-y-1">
              {menuItems.map(({ title, href, Icon }) => {
                const active = isActive(href);
                return (
                  <SidebarMenuItem key={title}>
                    <SidebarMenuButton
                      asChild
                      tooltip={title}
                      className={cn(
                        "transition-all duration-200 rounded-lg",
                        active
                          ? "bg-[hsl(var(--proveedor-primary))] text-white hover:bg-[hsl(var(--proveedor-primary-hover))] shadow-sm"
                          : "hover:bg-[hsl(var(--proveedor-accent-bg))] text-foreground"
                      )}
                    >
                      <Link to={href}>
                        <Icon
                          className={cn(
                            "h-4 w-4 transition-colors",
                            active
                              ? "text-white"
                              : "text-[hsl(var(--proveedor-accent))]"
                          )}
                        />
                        <span
                          className={cn(
                            "font-medium",
                            active ? "text-white" : "text-foreground"
                          )}
                        >
                          {title}
                        </span>
                        {active && (
                          <div className="ml-auto h-1.5 w-1.5 rounded-full bg-white/80" />
                        )}
                      </Link>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="border-t border-sidebar-border/50">
        <div className="p-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="group w-full flex items-center gap-3 p-2 rounded-xl bg-gradient-to-br from-white to-[hsl(var(--proveedor-surface))] hover:from-[hsl(var(--proveedor-accent-bg))] hover:to-white border border-transparent hover:border-[hsl(var(--proveedor-primary))]/10 shadow-sm hover:shadow-md transition-all duration-300 outline-none">
                <div className="relative shrink-0">
                  <div className="absolute -inset-0.5 bg-gradient-to-br from-[hsl(var(--proveedor-primary))] to-[hsl(var(--proveedor-accent))] rounded-full opacity-75 group-hover:opacity-100 transition-opacity" />
                  <div className="relative w-11 h-11 rounded-full bg-white p-0.5 flex items-center justify-center">
                    <div className="w-full h-full rounded-full bg-[hsl(var(--proveedor-surface))] flex items-center justify-center">
                      <User className="h-5 w-5 text-[hsl(var(--proveedor-primary))]" />
                    </div>
                  </div>
                </div>
                <div className="flex-1 text-left min-w-0">
                  <p className="text-[13px] font-semibold text-foreground truncate leading-tight">
                    {userName}
                  </p>
                  <p className="text-[11px] text-muted-foreground truncate leading-tight mt-0.5">
                    {userEmail}
                  </p>
                </div>
                <div className="shrink-0 w-6 h-6 rounded-lg bg-[hsl(var(--proveedor-surface))] group-hover:bg-[hsl(var(--proveedor-primary))]/10 flex items-center justify-center transition-colors">
                  <ChevronRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-[hsl(var(--proveedor-primary))] transition-colors" />
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" sideOffset={8} className="w-56">
              <DropdownMenuItem
                onClick={() => navigate("/perfil")}
                className="cursor-pointer"
              >
                <User className="mr-2 h-4 w-4" />
                Mi cuenta
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-red-600 focus:text-red-600 focus:bg-red-50 cursor-pointer"
              >
                <LogOut className="mr-2 h-4 w-4" />
                Cerrar sesion
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
